import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, TrendingUp, Heart, Users, Star, Briefcase, MessageCircle, Award, Clock } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { BenefitsGuideSection } from "../components/BenefitsGuideSection";
import { ICHRAGuideSection } from "../components/ICHRAGuideSection";
import { ICHRAFAQSection } from "../components/ICHRAFAQSection";

const practices = [
  {
    icon: Heart,
    title: "Offer Competitive Benefits",
    desc: "Employees consistently rank benefits as one of the top reasons they stay with an employer. Providing dental, vision, and supplemental insurance shows your team you are invested in their wellbeing, not just their output.",
    tips: ["Offer at least dental and vision coverage", "Consider supplemental plans for added security", "Communicate the value of benefits clearly during onboarding"],
  },
  {
    icon: MessageCircle,
    title: "Benefits Communication & Simple Explanation",
    desc: "Even the best benefits package fails if employees do not understand what they have. Clear, simple communication of benefits in plain language is one of the most underrated tools for attracting and retaining talent. When employees truly understand their coverage, they feel more secure, more valued, and more loyal to their employer.",
    tips: ["Walk employees through their benefits in plain language during onboarding", "Provide simple one-page summaries of each plan", "Make yourself available to answer benefits questions year-round", "Highlight the dollar value of benefits in offer letters and reviews"],
  },
  {
    icon: TrendingUp,
    title: "Competitive Compensation",
    desc: "Pay matters but total compensation matters more. Employees who feel their full package (salary plus benefits) is fair are significantly less likely to leave. Benefits can offset salary gaps with larger competitors.",
    tips: ["Benchmark salaries against your local market", "Include benefits value in offer letters", "Review compensation annually"],
  },
  {
    icon: Clock,
    title: "Work-Life Balance",
    desc: "Burnout is one of the leading causes of employee turnover. Offering flexibility, whether that is remote options, flexible hours, or generous PTO, signals that you respect your employees lives outside of work.",
    tips: ["Offer flexible scheduling where possible", "Encourage employees to use their PTO", "Lead by example as a business owner"],
  },
  {
    icon: Award,
    title: "Recognition & Appreciation",
    desc: "People want to feel seen. Regular recognition, even simple verbal acknowledgment, dramatically improves morale and retention. Employees who feel appreciated are 63% less likely to look for a new job.",
    tips: ["Recognize wins publicly in team settings", "Celebrate work anniversaries and milestones", "Make recognition specific and genuine"],
  },
  {
    icon: Briefcase,
    title: "Career Development",
    desc: "Employees who see a future with your company are far more loyal. Invest in their growth through training, mentorship, or simply mapping out a career path within your organization.",
    tips: ["Offer cross-training opportunities", "Set clear paths for advancement", "Support continuing education or certifications"],
  },
  {
    icon: MessageCircle,
    title: "Open Communication",
    desc: "Employees leave managers, not companies. Foster a culture where feedback flows both ways. Regular one on ones and honest conversations build the trust that keeps good people around.",
    tips: ["Hold regular check-ins with your team", "Act on employee feedback when possible", "Be transparent about company direction and changes"],
  },
  {
    icon: Users,
    title: "Strong Company Culture",
    desc: "Culture is the personality of your business. A positive, inclusive environment where people enjoy coming to work is one of the strongest retention tools available and it costs very little to build intentionally.",
    tips: ["Define and live your company values", "Create opportunities for team bonding", "Address toxic behaviors quickly and directly"],
  },
  {
    icon: Star,
    title: "Employee Wellness Programs",
    desc: "Healthy employees are happier, more productive, and more loyal. Even small investments in wellness, whether through supplemental insurance, gym benefits, or mental health support, show your team you care about the whole person.",
    tips: ["Offer supplemental health and accident insurance", "Promote mental health resources", "Consider wellness stipends or incentives"],
  },
];

const StatCard = ({ value, label }) => (
  <div className="text-center">
    <p className="font-bold text-4xl text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
    <p className="text-white text-sm opacity-80">{label}</p>
  </div>
);

const PracticeCard = ({ practice, index }) => (
  <div
    data-testid={`practice-card-${practice.title}`}
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all p-8"
  >
    <div className="flex items-start space-x-4 mb-4">
      <div className="w-12 h-12 bg-[#EAF9F9] rounded-xl flex items-center justify-center flex-shrink-0">
        <practice.icon size={24} className="text-[#2EC4C4]" />
      </div>
      <div>
        <span className="text-xs font-bold text-[#2EC4C4] uppercase tracking-wider">Tip #{index + 1}</span>
        <h3 className="font-bold text-xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {practice.title}
        </h3>
      </div>
    </div>
    <p className="text-[#475569] leading-relaxed mb-5">{practice.desc}</p>
    <ul className="space-y-2">
      {practice.tips.map((tip) => (
        <li key={tip} className="flex items-start space-x-2">
          <CheckCircle size={16} className="text-[#2EC4C4] flex-shrink-0 mt-0.5" />
          <span className="text-[#0F172A] text-sm">{tip}</span>
        </li>
      ))}
    </ul>
  </div>
);

const HeroSection = () => (
  <section data-testid="resources-hero" className="bg-[#1A2744] py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h1 className="font-bold text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Resources
      </h1>
      <p className="text-gray-300 text-lg max-w-2xl mx-auto">
        Practical insights to help small business owners attract, retain, and support their best employees.
      </p>
    </div>
  </section>
);

const StatsBanner = () => (
  <section className="bg-[#2EC4C4] py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        <StatCard value="57%" label="of workers say benefits are a top factor in job acceptance" />
        <StatCard value="3×" label="more likely to stay when employees feel valued" />
        <StatCard value="33%" label="of employees leave due to lack of growth opportunities" />
        <StatCard value="$15k+" label="average cost to replace a single employee" />
      </div>
    </div>
  </section>
);

const IntroSection = () => (
  <section className="py-16 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
        8 Best Practices for Retaining Employees
      </h2>
      <p className="text-[#475569] text-lg leading-relaxed">
        Hiring great people is hard. Keeping them is even harder, especially for small businesses competing against larger employers. The good news? Retention does not require a massive budget. It requires intention, consistency, and the right benefits package.
      </p>
    </div>
  </section>
);

const PracticesGrid = () => (
  <section data-testid="practices-grid" className="pb-20 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {practices.map((practice, i) => (
          <PracticeCard key={practice.title} practice={practice} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section data-testid="resources-cta" className="py-20 bg-[#1A2744]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-bold text-3xl lg:text-4xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Ready to Strengthen Your Benefits Package?
      </h2>
      <p className="text-gray-300 text-lg mb-8">
        A strong benefits package is one of the most powerful retention tools you have. Let us help you build one that fits your budget.
      </p>
      <Link
        to="/contact"
        data-testid="resources-cta-btn"
        className="inline-flex items-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-8 py-4 text-base transition-all shadow-md hover:shadow-lg active:scale-95"
      >
        <span>Get a Free Quote</span>
        <ArrowRight size={18} />
      </Link>
    </div>
  </section>
);

export default function ResourcesPage() {
  usePageMeta({
    title: "ICHRA Guide & Employee Retention Resources for ND Small Businesses | Viall Business Solutions",
    description: "Free guide to ICHRA (Individual Coverage HRA) and 8 best practices for retaining employees. Practical advice from local North Dakota benefits experts for small businesses.",
    path: "/resources",
  });
  return (
    <div className="pt-16 lg:pt-20">
      <HeroSection />
      <StatsBanner />
      <BenefitsGuideSection />
      <ICHRAGuideSection />
      <ICHRAFAQSection />
      <IntroSection />
      <PracticesGrid />
      <CTASection />
    </div>
  );
}
